// Browser side of signing in from inside Helm's frame: Microsoft's sign-in page refuses to be
// framed, so the framed login page runs it in a popup and waits to hear back
// (`src/lib/popup-complete.ts` is the page the popup lands on).

import { z } from "zod";
import { POPUP_SIGN_IN_MESSAGE } from "@/lib/embedding";

/**
 * Is the app running inside another site's frame? A cross-origin `window.top` read that
 * throws counts as framed, failing towards the popup — sending a framed page to Microsoft
 * directly would only show the browser's "refused to connect" page.
 */
export function isEmbedded(): boolean {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
}

const popupMessageSchema = z.object({
  type: z.literal(POPUP_SIGN_IN_MESSAGE),
  error: z.string().nullable(),
});

/**
 * The popup's report, or null for any other message. The origin check is the trust boundary:
 * Helm (which frames us) runs on another origin and cannot forge `event.origin`, whatever it
 * puts in `data`. Takes plain arguments so it is testable without a DOM.
 */
export function readPopupMessage(
  eventOrigin: string,
  data: unknown,
  ownOrigin: string,
): { error: string | null } | null {
  if (eventOrigin !== ownOrigin) return null;
  const parsed = popupMessageSchema.safeParse(data);
  return parsed.success ? { error: parsed.data.error } : null;
}

/**
 * Calls `onClosed` once, when `popup` closes, and stops. Returns a cancel function. Each call
 * owns its own interval, so an abandoned watch can never keep firing.
 */
export function watchPopupClosed(
  popup: { readonly closed: boolean },
  onClosed: () => void,
  intervalMs = 500,
): () => void {
  const id = setInterval(() => {
    if (popup.closed) {
      clearInterval(id);
      onClosed();
    }
  }, intervalMs);
  return () => clearInterval(id);
}
